import { BalanceText, Text, HStack, Stack, VStack } from '@genial/components';
import { BoxRadius, Spacing, Typography } from '@genial/design-tokens';
import { Utils, useBreakpointValue } from '@genial/utils';
import React from 'react';

import {
  TableContentItemBadge,
  TableContentItemCaptions,
} from '../../../../../../../../components/TableContent';
import { messageError } from '../../../../../../../../store/batchTicket/constants';
import { useBasketDetailsInfoResume } from '../../../../../../hooks/basketDetailsInfoResume';
import { mapCaptions } from '../../../../../../utils';

interface ResumeDetailsInfoProps {}

const WrapperContent = ({ children }) => (
  <Stack
    direction={{ base: 'column', sm: 'row' }}
    w="full"
    space={Spacing['stack.nano']}
    alignItems={{ sm: 'center' }}
    justifyContent={{ sm: 'space-between' }}>
    {children}
  </Stack>
);

export const ResumeDetailsInfo: React.FC<ResumeDetailsInfoProps> = () => {
  const Wrapper = useBreakpointValue({
    base: WrapperContent,
    lg: ({ children }) => children,
  });

  const {
    basketCustomerOrderItem,
    isMobile,
    assetQuantityInfo,
    routeParamTypeIsTotalSummary,
    handleUpdateBalance,
    isLoadingBalance,
    availableBalance,
    allocatedBalanceField,
    remainingBalanceField,
  } = useBasketDetailsInfoResume();

  return (
    <Stack
      direction={{ base: 'column', lg: 'row' }}
      space={Spacing['stack.nano']}
      px={Spacing['inline.xxs']}
      py={Spacing['inline.xxxs']}
      borderRadius={BoxRadius.sm}
      alignItems="center"
      justifyContent="space-between"
      bgColor={Utils.ColorToken.WithOpacity('brand.tertiary.lightest', 0.3)}>
      <Wrapper>
        <HStack alignItems="center" justifyContent="space-between">
          <VStack justifyContent="space-between" space={Spacing['inline.quarck']}>
            {!isMobile && (
              <Text fontSize={Typography['fontsize.md']} color="neutral.black" variant="semibold">
                Nome
              </Text>
            )}
            <Text noOfLines={1} fontSize={Typography['fontsize.md']} color="neutral.darkest">
              {basketCustomerOrderItem?.customer?.customerName || messageError}
            </Text>
            <HStack space={Spacing['inline.nano']} alignItems="center">
              <Text fontSize={Typography['fontsize.xs']} color="neutral.dark">
                Conta
              </Text>
              <Text fontSize={Typography['fontsize.xs']} color="neutral.dark">
                {basketCustomerOrderItem?.customer?.account?.accountNumber || messageError}
              </Text>
              <TableContentItemCaptions
                forceDesktop
                icons={mapCaptions(basketCustomerOrderItem?.customer || [])}
              />
            </HStack>
          </VStack>
          {isMobile && (
            <TableContentItemBadge forceDesktop={!isMobile}>
              {basketCustomerOrderItem?.customer?.suitabilityProfileName || messageError}
            </TableContentItemBadge>
          )}
        </HStack>
        {!isMobile && (
          <Stack
            direction={{ base: 'row', sm: 'column' }}
            space={Spacing['inline.quarck']}
            justifyContent="space-between">
            <Text
              fontSize={Typography['fontsize.md']}
              display={{ base: 'none', lg: 'flex' }}
              color="neutral.black"
              variant="semibold">
              Suitability
            </Text>
            <TableContentItemBadge forceDesktop={!isMobile}>
              {basketCustomerOrderItem?.customer?.suitabilityProfileName || messageError}
            </TableContentItemBadge>
          </Stack>
        )}
      </Wrapper>
      <Wrapper>
        <Stack
          direction={{ base: 'row', sm: 'column' }}
          space={Spacing['inline.quarck']}
          justifyContent="space-between"
          w={{ base: 'full', sm: 'container' }}>
          <Text fontSize={Typography['fontsize.md']} color="neutral.black" variant="semibold">
            {assetQuantityInfo.label}
          </Text>
          <Text noOfLines={1} fontSize={Typography['fontsize.md']} color="neutral.darkest">
            {assetQuantityInfo.value}
          </Text>
        </Stack>
        <Stack
          direction={{ base: 'row', sm: 'column' }}
          space={Spacing['inline.quarck']}
          justifyContent="space-between"
          w={{ base: 'full', sm: 'container' }}>
          <Text fontSize={Typography['fontsize.md']} color="neutral.black" variant="semibold">
            Saldo disponível
          </Text>
          {!isMobile && (
            <BalanceText
              balance={availableBalance}
              hasUpdateButton={!isMobile}
              onUpdateButtonPress={handleUpdateBalance}
              isUpdating={isLoadingBalance}
              lastUpdatedAt={new Date()}
              textAlign={'flex-start'}
              positiveBalanceColor="neutral.black"
              negativeBalanceColor="feedback.error.default"
            />
          )}
          {isMobile && (
            <Text noOfLines={1} fontSize={Typography['fontsize.md']} color="neutral.darkest">
              {Utils.Mask.Money(availableBalance)}
            </Text>
          )}
        </Stack>
        <Stack
          direction={{ base: 'row', sm: 'column' }}
          space={Spacing['inline.quarck']}
          justifyContent="space-between"
          {...(routeParamTypeIsTotalSummary && { alignItems: 'flex-start' })}
          w={{ base: 'full', sm: 'container' }}>
          <Text fontSize={Typography['fontsize.md']} color="neutral.black" variant="semibold">
            Saldo alocado | %
          </Text>
          <HStack alignItems="flex-end" space={Spacing['inline.quarck']}>
            <Text noOfLines={1} fontSize={Typography['fontsize.md']} color="neutral.darkest">
              {allocatedBalanceField}
            </Text>
          </HStack>
        </Stack>
        <Stack
          direction={{ base: 'row', sm: 'column' }}
          space={Spacing['inline.quarck']}
          alignItems={{ base: 'center', sm: 'flex-end' }}
          justifyContent="space-between"
          w={{ base: 'full', sm: 'container' }}>
          <Text fontSize={Typography['fontsize.md']} color="neutral.black" variant="semibold">
            Saldo restante | %
          </Text>
          <HStack alignItems="flex-end" space={Spacing['inline.quarck']}>
            <Text noOfLines={1} fontSize={Typography['fontsize.md']} color="neutral.darkest">
              {remainingBalanceField}
            </Text>
          </HStack>
        </Stack>
      </Wrapper>
    </Stack>
  );
};
